import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Helmet } from "react-helmet";
import { useSearchParams } from "react-router-dom";
import moment from "moment";
import { Outlet, NavLink, useNavigate } from "react-router-dom";

// components
import { SectionHeader } from "../../components/layouts/section.header";
import { BadgeServiceType } from "../../components/commons/badge.service.type";
import { BadgeServiceStatus } from "../../components/commons/badge.service.status";
import { BadgePremiseType } from "../../components/commons/badge.premise.type";
import { BadgePremisePhaseType } from "../../components/commons/badge.premise.phase.type";

// drawer
import { DrawerPartnerUploadedDocument } from "../../components/drawers/partner.uploaded.document";

// reducers
import { fetchJobServiceDetails, resetJobReports } from "../../stores/jobs";

// utilities
import { classNames } from "../../utilities";

export const ScreenJobDetails = () => {

  // hooks
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const user_id = searchParams.get('user_id');
  const job_id = searchParams.get('job_id');
  const { details } = useSelector(context => context.jobs);
  console.log('details', details)

  // states
  const [mounted, setMounted] = useState(false)
  const [drawerUploadedDoc, setDrawerUploadedDoc] = useState({
    open: false,
    url: null,
  });

  const loaddata = async ({user_id, job_id}) => {
    try {
      dispatch(resetJobReports())
      const res = await dispatch(fetchJobServiceDetails({
        user_id: user_id,
        job_id: job_id
      })).unwrap();
      console.debug('screens.partners.details@loaddata', 'res', res);
    } catch (err) {
      console.error('screens.partners.details@loaddata', err);
    }
  }

  useEffect(() => {
    // sanity check
    if (!mounted) return;
    if (!user_id) return;
    if (!job_id) return;

    loaddata({
      user_id,
      job_id
    });
  }, [mounted, user_id, job_id])

  useEffect(() => {
    setMounted(true)
  }, [])


  if (!details) return null;
  return (
    <>
      <Helmet>
        <title>Job Details</title>
      </Helmet>
      <div className="py-8 h-screen w-full">

        {/* Header */}
        <div className="pb-8 px-4 grid grid-cols-10 gap-4 border-b border-slate-200">

          <div className="col-span-6 flex flex-col justify-between items-start">
            <div className="flex flex-row justify-start items-center">
              <SectionHeader title={details.job_id} />
              <div className="px-1 pl-4">
                <BadgeServiceType service_type={details.service_type}/>
              </div>
              <div className="px-1">
                <BadgeServiceStatus status={details.status}/>
              </div>
            </div>
            
            <div className="mt-4 flex flex-row justify-end items-end">
              <HeaderKeyValue label={'Created on'} value={moment(details.created).format('llll')} />
              <HeaderKeyValue label={'Region'} value={`${details.premise?.place?.municipality}, ${details.premise?.place?.region}`} />
            </div>
          </div>
        </div>

        {/* BODY */}
        <div className="h1-full grid grid-cols-12 gap-4 bg-slate-50">

          {/* WORKING */}
          <div className="col-span-8 px-4 py-4">

            {/* FILTER */}
            <div className="flex flex-row justify-start items-center">
              <NavLink to='paymentdetails'>
                {(isActive) => (
                  <FilterSection title={'Payment'} selected={isActive} />
                )}
              </NavLink>
              <NavLink to='partner'>
                {(isActive) => (
                  <FilterSection title={'Strategic Partner'} selected={isActive} />
                )}
              </NavLink>
              <NavLink to='reports'>
                {(isActive) => (
                  <FilterSection title={'Reports'} selected={isActive} />
                )}
              </NavLink>
            </div>

            {/* SECTION OUTLET */}
            <div className="py-8">
              <Outlet />
            </div>
            
          </div>

          {/* INFORMATION */}
          <div className="col-span-4 overflow-y-auto bg-white border-l border-slate-200 shadow-md">

            {/* PERSONAL */}
            <div className="p-8 flex flex-col border-b border-slate-200">
              <h2 className="pb-4 text-base text-slate-700 font-semibold">Customer</h2>
              <SideKeyValue label={'Name'} value={details.customer.name} />
              <SideKeyValue label={'Phone Number'} value={details.customer.phone_number} />
              <SideKeyValue label={'Email'} value={details.customer.email} />
              <SideKeyValue label={'Identifier'} value={details.customer.user_id} />
            </div>

            {/* PREMISE */}
            <div className="p-8 flex flex-col border-b border-slate-200">
              <h2 className="pb-4 text-base text-slate-700 font-semibold">Premise</h2>
              <SideKeyValue label={'Name'} value={details.premise.name} />
              <SideKeyValue label={'Type'} value={<BadgePremiseType type={details.premise.type}/>} />
              <SideKeyValue label={'Meter Type'} value={<BadgePremisePhaseType phase_type={details.premise.phase_type}/>} />
              <SideKeyValue 
                label={'Address'} 
                value={
                  <div className="flex flex-col">
                    <p>{details.premise.address.address1}</p>
                    <p>{details.premise.address.address2}</p>
                    <p>{details.premise.address.poscode}, {details.premise.address.city}</p>
                    <p>{details.premise.address.state}</p>
                  </div>
                } 
              />
              <SideKeyValue label={'Created'} value={moment(details.premise.created).format('llll')} />
              <SideKeyValue label={'Last Modified'} value={moment(details.premise.modified).format('llll')} />
            </div>

          </div>
        </div>
        
      </div>

      {/* DRAWERS */}
      <DrawerPartnerUploadedDocument 
        open={drawerUploadedDoc.open}
        setOpen={() => setDrawerUploadedDoc({open: false, url: null})}
      />
    </>
  );
};

const HeaderKeyValue = ({label, value}) => {
  return (
    <div className="pr-8">
      <h4 className="text-sm text-slate-500 font-normal">{label}</h4>
      <p className="text-base text-slate-800 font-semibold">{value}</p>
    </div>
  )
}


const SideKeyValue = ({label, value}) => {
  return (
    <div className="pt-4 grid grid-cols-8">
      <h4 className="col-span-4 text-sm text-slate-500 font-normal">{label}</h4>
      <span className="col-span-4 text-sm text-slate-900 font-medium text-left">{value}</span>
    </div>
  )
}

const FilterSection = ({title, selected, onSelect = () => {}}) => {
  return (
    <div className={classNames(
      "px-8 py-1 mr-4 border border-slate-200 rounded-xl shadow-sm hover:cursor-pointer",
      selected ? 'bg-primary-500' : 'bg-slate-200',
    )}>
      <p className={classNames(
        "col-span-4 text-sm font-normal",
        selected ? 'text-white': 'text-slate-500'
      )}
      >
        {title}
      </p>
    </div>
  )
}